import React from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Cpu, Wrench, Building2, Users, Shield, Cable, Lightbulb, 
  GitBranch, CheckCircle, Settings, Power, Zap
} from 'lucide-react';

import img1 from "../Images/allinone.png";
import img2 from "../Images/surilogo.png";
import img3 from "../Images/algologo.png";

const EnterpriseSection = () => {
  const handleNavigate = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/contact';
    }
  };

  const capabilities = [
    {
      icon: Power,
      title: "Complete Electrical Solutions",
      description: "Comprehensive electrical engineering and installation services backed by our expert team of certified electrical engineers and technicians.",
      gradient: "from-blue-500 to-cyan-400"
    },
    {
      icon: Building2,
      title: "Industrial System Integration",
      description: "End-to-end industrial automation and control systems implementation for maximum operational efficiency and productivity.",
      gradient: "from-indigo-500 to-blue-400"
    },
    {
      icon: Users,
      title: "Expert Workforce",
      description: "Large team of certified engineers and skilled technicians delivering excellence in industrial installations across Nepal.",
      gradient: "from-cyan-500 to-blue-400"
    },
    {
      icon: Shield,
      title: "Trusted Partnerships",
      description: "Official partner and authorized distributor for leading global manufacturers and international technology providers.",
      gradient: "from-blue-500 to-indigo-400"
    }
  ];

  const services = [
    {
      icon: Cpu,
      title: "System Design & Engineering",
      points: [
        "Load calculation & power analysis",
        "AutoCAD electrical drawings",
        "Protection system design",
        "Power distribution planning",
        "Energy audit services"
      ]
    },
    {
      icon: Wrench,
      title: "Installation & Commissioning",
      points: [
        "HT/LT panel installation",
        "Power and Process Boiler Installation",
        "Water & Waste Treatment Plants",
        "Industrial electrical wiring",
        "Pollution Control Equipment"
      ]
    },
    {
      icon: Settings,
      title: "Automation & Control Systems",
      points: [
        "PLC programming services",
        "SCADA system implementation",
        "Motor control centers (MCC)",
        "Building automation systems",
        "Industrial sensors & controllers"
      ]
    }
  ];

  const partners = [
    {
      name: "Algo Pumps",
      logo: img3,
      description: "World-class pumping solutions and fluid management systems with technologically integrated and inventive pumping equipment for industrial applications.",
      specialties: ["Industrial Pumps", "Water Treatment Systems"]
    },
    {
      name: "Suri Engineers",
      logo: img2,
      description: "Manufacturer of world-class food processing equipment serving rice millers across the Indian sub-continent with advanced milling technology.",
      specialties: ["Food Processing", "Rice Milling Equipment"]
    }
  ];

  // Structured data
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Enterprise Industrial Automation Solutions",
    "description": "Comprehensive electrical engineering, automation, and industrial system integration services across Nepal",
    "provider": {
      "@type": "Organization",
      "name": "Gortherm Energy"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Nepal"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Industrial Engineering Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Electrical System Design and Engineering"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Industrial Installation and Commissioning"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Automation and Control Systems"
          }
        }
      ]
    }
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema, null, 2)}
        </script>
      </Helmet>

      <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 py-16 md:py-20 overflow-hidden" aria-label="Enterprise industrial automation solutions">
        {/* Background Decorations */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob" />
          <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-cyan-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000" />
          <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000" />
        </div>

        {/* Main Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Hero Section */}
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16 mb-16 md:mb-20">
            
            {/* Left Image */}
            <div className="w-full lg:w-2/5">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-3xl opacity-20 blur-lg transform -rotate-6" aria-hidden="true" />
                <img
                  src={img1}
                  alt="Gortherm Energy enterprise industrial automation solutions showing electrical systems and control panels installation"
                  className="relative rounded-3xl shadow-2xl object-cover w-full h-[400px] sm:h-[500px]"
                  loading="lazy"
                  width="600"
                  height="500"
                />
                <div className="absolute -top-4 sm:-top-6 -right-4 sm:-right-6 p-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl animate-pulse" aria-hidden="true">
                  <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </div>
              </div>
            </div>

            {/* Right Content */}
            <article className="w-full lg:w-3/5">
              <header>
                <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 md:mb-8 bg-gradient-to-r from-black to-red-900 bg-clip-text text-transparent leading-tight">
                  Enterprise-Grade Industrial Solutions
                </h2>
              </header>
              
              <div className="space-y-4">
                <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
                  Leading Electrical Engineering and Automation Services in Nepal
                </h3>
                
                <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed">
                  With decades of experience and a robust workforce of certified professionals, we deliver comprehensive <strong>electrical engineering</strong>, <strong>industrial automation</strong>, and <strong>system integration solutions</strong> across Nepal's dynamic industrial landscape. Our unwavering commitment to excellence drives us to empower industries with cutting-edge technologies and dependable services.
                </p>
                
                <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed">
                  Our team of highly skilled <strong>electrical engineers</strong> and <strong>automation technicians</strong> combines technical expertise with innovative thinking to design and implement solutions that enhance efficiency, reliability, and productivity. From initial <strong>system design consultation</strong> to <strong>installation</strong>, <strong>commissioning</strong>, <strong>maintenance</strong>, and ongoing <strong>technical support</strong>, we ensure seamless execution and exceptional service delivery at every stage.
                </p>
                
                <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed">
                  As an authorized partner for leading global manufacturers, we provide access to world-class equipment and proven technology solutions for <strong>industrial electrical systems</strong>, <strong>automation control</strong>, <strong>boiler installations</strong>, <strong>water treatment plants</strong>, and <strong>pollution control equipment</strong>.
                </p>
              </div>
            </article>
          </div>

          {/* Capabilities Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-16 md:mb-20">
            {capabilities.map((item, index) => (
              <article 
                key={index} 
                className="relative group transition-transform duration-300 ease-in-out transform hover:scale-105"
              >
                <div className={`absolute inset-0 bg-gradient-to-r ${item.gradient} rounded-2xl opacity-0 group-hover:opacity-80 transition-opacity duration-300`} aria-hidden="true" />
                <div className="relative p-6 bg-white/90 backdrop-blur-sm rounded-2xl border border-blue-100 shadow-lg hover:shadow-xl">
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-r ${item.gradient} text-white mb-4`} aria-hidden="true">
                    <item.icon className="w-6 h-6" />
                  </div>
                  <h4 className="text-lg sm:text-xl font-semibold mb-2 text-gray-900">{item.title}</h4>
                  <p className="text-sm sm:text-base text-gray-600">{item.description}</p>
                </div>
              </article>
            ))}
          </div>

          {/* Technical Services */}
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl overflow-hidden border border-blue-100 mb-16 md:mb-20">
            <header className="p-6 sm:p-8 bg-gradient-to-r from-sky-600 to-sky-900 text-white">
              <h3 className="text-xl sm:text-2xl font-bold mb-2">Technical Excellence in Industrial Engineering</h3>
              <p className="text-sm sm:text-base text-blue-100">Comprehensive electrical engineering and automation services backed by certified professionals</p>
            </header>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 p-6 sm:p-8">
              {services.map((service, index) => (
                <article 
                  key={index} 
                  className="group space-y-4 p-6 rounded-xl bg-white/90 hover:bg-blue-50/50 transition-colors duration-300 shadow-md hover:shadow-xl"
                >
                  <h4 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center">
                    <div className="p-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg text-white mr-3 group-hover:scale-110 transition-transform duration-300 flex-shrink-0" aria-hidden="true">
                      <service.icon className="w-5 h-5" />
                    </div>
                    <span>{service.title}</span>
                  </h4>
                  <ul className="space-y-3">
                    {service.points.map((point, idx) => (
                      <li key={idx} className="flex items-start text-sm sm:text-base text-gray-600 group-hover:text-gray-900 transition-colors duration-300">
                        <CheckCircle className="w-4 h-4 mr-2 text-blue-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>

          {/* Partner Brands Section */}
          <div className="mb-16 md:mb-20">
            <header className="text-center mb-8 md:mb-12">
              <h3 className="text-2xl sm:text-3xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Authorized Partners & Distributors
              </h3>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                We partner with leading global manufacturers to bring world-class industrial equipment and technology solutions to Nepal
              </p>
            </header>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
              {partners.map((partner, index) => (
                <article 
                  key={partner.name}
                  className="group relative bg-white/80 backdrop-blur-sm rounded-xl shadow-md border border-blue-100 
                           hover:shadow-xl transition-all duration-500 ease-in-out overflow-hidden"
                >
                  {/* Logo Container */}
                  <div className="h-32 sm:h-40 p-4 sm:p-6 flex items-center justify-center relative bg-white">
                    <img
                      src={partner.logo}
                      alt={`${partner.name} - Official partner for ${partner.specialties.join(' and ')}`}
                      className="w-40 sm:w-48 h-20 sm:h-24 object-contain transition-transform duration-500 ease-in-out
                               group-hover:scale-110"
                      loading="lazy"
                    />
                  </div>
                  
                  {/* Content Container */}
                  <div className="p-4 sm:p-6 bg-gradient-to-br from-blue-50 to-indigo-50">
                    <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 
                                 transition-colors duration-300 group-hover:text-blue-600">
                      {partner.name}
                    </h4>
                    
                    <p className="text-sm text-gray-600 mb-4 transition-colors duration-300
                                group-hover:text-gray-700">
                      {partner.description}
                    </p>
                    
                    {/* Specialties */}
                    <div className="flex flex-wrap gap-2">
                      {partner.specialties.map((specialty, idx) => (
                        <span 
                          key={idx}
                          className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-600
                                   transition-colors duration-300 group-hover:bg-blue-200"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>

          {/* Additional SEO Content */}
          <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-3xl p-6 sm:p-8 md:p-12 mb-16">
            <h3 className="text-2xl sm:text-3xl font-bold text-center mb-8 text-gray-900">
              Why Choose Our Enterprise Solutions?
            </h3>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-lg font-semibold mb-3 text-gray-900">Certified Professionals</h4>
                <p className="text-sm sm:text-base text-gray-700">
                  Team of licensed electrical engineers, automation specialists, and certified technicians with extensive industry experience.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-lg font-semibold mb-3 text-gray-900">Turnkey Solutions</h4>
                <p className="text-sm sm:text-base text-gray-700">
                  Complete project management from design and procurement to installation, testing, and commissioning with ongoing support.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-lg font-semibold mb-3 text-gray-900">Quality Equipment</h4>
                <p className="text-sm sm:text-base text-gray-700">
                  Authorized distributor for premium international brands ensuring genuine products with manufacturer warranties and support.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-lg font-semibold mb-3 text-gray-900">Safety Compliance</h4>
                <p className="text-sm sm:text-base text-gray-700">
                  All installations meet international safety standards and local electrical codes with proper documentation and certifications.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-lg font-semibold mb-3 text-gray-900">24/7 Support</h4>
                <p className="text-sm sm:text-base text-gray-700">
                  Round-the-clock technical support, emergency response teams, and preventive maintenance programs across Nepal.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-lg font-semibold mb-3 text-gray-900">Cost-Effective</h4>
                <p className="text-sm sm:text-base text-gray-700">
                  Competitive pricing with transparent quotations, energy-efficient solutions, and long-term operational cost savings.
                </p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <button
              className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 rounded-xl bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 text-white text-sm sm:text-base font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              onClick={handleNavigate}
              aria-label="Contact us to start your industrial automation project"
            >
              <Lightbulb className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Start Your Industrial Project
            </button>
            <p className="mt-4 text-sm sm:text-base text-gray-600">
              Get expert consultation for your electrical and automation requirements
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default EnterpriseSection;